<template src="./BulkProcess.html"></template>

<script>
import { mapGetters } from "vuex";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  getServiceByCompany,
  createProcessesBulk,
} from "../../../services";
import Companies from "../../../components/Forms/Companies/Companies";
import ServiceSelector from "./ServiceSelector";
import { OnlyRisk } from "../../../utils/roles";

// CSV column -> API field. The order here defines the template column order.
const COLUMNS = [
  { csv: "nombre_completo", field: "fullName", label: "Nombre completo", required: true },
  { csv: "tipo_documento", field: "typeDocument", label: "Tipo de documento", required: true },
  { csv: "documento_identidad", field: "documentId", label: "Documento de identidad", required: true },
  { csv: "ciudad", field: "city", label: "Ciudad", required: true },
  { csv: "correo", field: "email", label: "Correo", required: false },
  { csv: "telefono", field: "phoneNumber", label: "Número de teléfono", required: false },
  { csv: "direccion", field: "address", label: "Dirección", required: false },
  { csv: "fecha_expedicion", field: "expeditionDate", label: "Fecha de expedición (YYYY-MM-DD)", required: false },
  { csv: "cargo", field: "position", label: "Cargo al que se postula", required: false },
  { csv: "observacion", field: "observation", label: "Observación", required: false },
  { csv: "centro_costos", field: "costCenter", label: "Centro de costos", required: false },
];

// Accepted document codes (case-insensitive) mapped to the exact backend enum casing.
const DOC_MAP = { CC: "CC", CE: "CE", PA: "PA", PT: "PT", NIT: "Nit" };
const VALID_DOCS = "CC, CE, PA, Nit, PT";
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PREVIEW_THRESHOLD = 30;

export default {
  name: "BulkProcess",
  components: { Companies, ServiceSelector },
  data: () => ({
    columns: COLUMNS,
    validDocs: VALID_DOCS,
    previewThreshold: PREVIEW_THRESHOLD,
    file: null,
    dragOver: false,
    parsing: false,
    parsed: false,
    validRows: [],
    errorRows: [],
    company: null,
    companyId: null,
    servicesByCompany: [],
    groupServicesByCompany: [],
    selectedServices: [],
    processing: false,
    result: null,
  }),
  computed: {
    ...mapGetters({ user: "getUser" }),
    OnlyRisk,
    showCompanySelector() {
      return OnlyRisk()(this.user.rol);
    },
    totalRows() {
      return this.validRows.length + this.errorRows.length;
    },
    hasErrors() {
      return this.errorRows.length > 0;
    },
    canProcess() {
      return (
        this.parsed &&
        !this.hasErrors &&
        this.validRows.length > 0 &&
        !!this.companyId &&
        this.selectedServices.length > 0 &&
        !this.processing &&
        !this.result
      );
    },
    showRowDetail() {
      return this.totalRows > 0 && this.totalRows <= this.previewThreshold;
    },
  },
  methods: {
    // ----- Company & services (reused from the single-create flow) -----
    async getCompanyServices(company) {
      try {
        this.companyId = company?.id || null;
        this.selectedServices = [];
        this.servicesByCompany = [];
        this.groupServicesByCompany = [];
        if (!this.companyId) return;

        const [servicesByCompany, ...groupServicesByCompany] = await getServiceByCompany(this.companyId, true);
        this.servicesByCompany = servicesByCompany || [];
        this.groupServicesByCompany = groupServicesByCompany || [];

        // Auto-select when the company has a single individual service and no groups.
        if (this.servicesByCompany.length === 1 && !this.groupServicesByCompany.length) {
          this.selectedServices = [this.servicesByCompany[0]];
        }
      } catch (err) {
        console.error("Error on list company services:", err);
      }
    },
    selectService(service, toggle) {
      this.selectedServices = service.length ? service : [service];
      if (toggle) toggle();
    },
    isServiceActive(service) {
      const id = service.length ? service[0].id : service.id;
      return this.selectedServices.some((s) => s.id === id);
    },

    // ----- Template download -----
    // Single source of truth for both the CSV and the Excel templates.
    templateHeaders() {
      return this.columns.map((c) => c.csv);
    },
    templateExample() {
      return [
        "Juan Pérez", "CC", "1128270002", "Medellín", "juan.perez@empresa.com",
        "3001234567", "Calle 1 # 2-3", "2015-06-20", "Analista", "Sin observaciones", "CC-001",
      ];
    },
    downloadTemplate() {
      const csv = Papa.unparse({ fields: this.templateHeaders(), data: [this.templateExample()] });
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "plantilla-carga-masiva-procesos.csv");
    },
    downloadTemplateExcel() {
      const worksheet = XLSX.utils.aoa_to_sheet([this.templateHeaders(), this.templateExample()]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Procesos");
      const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "plantilla-carga-masiva-procesos.xlsx");
    },

    // ----- File handling -----
    onDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) this.handleFile(file);
    },
    onSelectFile(event) {
      const file = event.target.files[0];
      if (file) this.handleFile(file);
      event.target.value = "";
    },
    handleFile(file) {
      const name = file.name.toLowerCase();
      const isCsv = name.endsWith(".csv");
      const isExcel = name.endsWith(".xls") || name.endsWith(".xlsx");
      if (!isCsv && !isExcel) {
        this.$swal({ text: "El archivo debe ser .csv, .xls o .xlsx", icon: "warning", buttons: false, timer: 3000 });
        return;
      }
      this.file = file;
      if (isExcel) this.parseExcel(file);
      else this.parseFile(file);
    },
    clear() {
      this.file = null;
      this.parsed = false;
      this.validRows = [];
      this.errorRows = [];
      this.result = null;
    },

    // ----- Parsing & validation (Step 1, all-or-nothing) -----
    parseFile(file) {
      this.parsing = true;
      this.parsed = false;
      this.result = null;
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim().toLowerCase(),
        complete: (res) => {
          this.validate(res.data, res.meta.fields || []);
          this.parsing = false;
          this.parsed = true;
        },
        error: (err) => {
          this.parsing = false;
          console.error("CSV parse error:", err);
          this.$swal({ text: "No se pudo leer el archivo CSV.", icon: "error", buttons: false, timer: 3000 });
        },
      });
    },
    // Parses .xls/.xlsx using the same header normalization and validation as the CSV flow.
    parseExcel(file) {
      this.parsing = true;
      this.parsed = false;
      this.result = null;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          // header: 1 -> array of rows; raw: false -> formatted strings (dates, numbers as text).
          const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
          const { data, fields } = this.matrixToObjects(matrix);
          this.validate(data, fields);
          this.parsing = false;
          this.parsed = true;
        } catch (err) {
          this.parsing = false;
          console.error("Excel parse error:", err);
          this.$swal({ text: "No se pudo leer el archivo Excel.", icon: "error", buttons: false, timer: 3000 });
        }
      };
      reader.onerror = (err) => {
        this.parsing = false;
        console.error("Excel read error:", err);
        this.$swal({ text: "No se pudo leer el archivo Excel.", icon: "error", buttons: false, timer: 3000 });
      };
      reader.readAsArrayBuffer(file);
    },
    // Converts a sheet matrix into { data, fields } shaped like Papa's header:true output,
    // mirroring transformHeader (trim + lowercase) and skipEmptyLines.
    matrixToObjects(matrix) {
      if (!matrix.length) return { data: [], fields: [] };
      const fields = (matrix[0] || []).map((h) => (h == null ? "" : h.toString().trim().toLowerCase()));
      const data = [];
      for (let i = 1; i < matrix.length; i += 1) {
        const cells = matrix[i] || [];
        const isEmpty = !cells.some((c) => c != null && c.toString().trim() !== "");
        if (isEmpty) continue;
        const obj = {};
        fields.forEach((f, idx) => {
          obj[f] = cells[idx] != null ? cells[idx] : "";
        });
        data.push(obj);
      }
      return { data, fields };
    },
    validate(data, fields) {
      const valid = [];
      const errors = [];

      const missingColumns = this.columns
        .filter((c) => c.required && !fields.includes(c.csv))
        .map((c) => c.csv);
      if (missingColumns.length) {
        this.validRows = [];
        this.errorRows = [];
        this.$swal({
          title: "Faltan columnas requeridas",
          text: `El archivo no incluye: ${missingColumns.join(", ")}. Descarga la plantilla.`,
          icon: "error",
          buttons: false,
          timer: 5000,
        });
        return;
      }

      data.forEach((raw, index) => {
        const rowNumber = index + 2; // +2: header row + 1-based
        const rowErrors = [];
        const row = {};

        this.columns.forEach((col) => {
          const value = (raw[col.csv] || "").toString().trim();
          if (col.required && !value) {
            rowErrors.push(`${col.label} es obligatorio`);
          }
          row[col.field] = value || null;
        });

        // Document type normalization to the backend enum casing.
        if (row.typeDocument) {
          const normalized = DOC_MAP[row.typeDocument.toUpperCase()];
          if (!normalized) {
            rowErrors.push(`tipo_documento "${row.typeDocument}" no es válido. Usa: ${VALID_DOCS}`);
          } else {
            row.typeDocument = normalized;
          }
        }

        if (row.expeditionDate && !DATE_REGEX.test(row.expeditionDate)) {
          rowErrors.push(`fecha_expedicion "${row.expeditionDate}" debe tener formato YYYY-MM-DD`);
        }
        if (row.email && !EMAIL_REGEX.test(row.email)) {
          rowErrors.push(`correo "${row.email}" no es válido`);
        }

        if (rowErrors.length) {
          errors.push({ row: rowNumber, fullName: row.fullName, documentId: row.documentId, errors: rowErrors });
        } else {
          valid.push(row);
        }
      });

      this.validRows = valid;
      this.errorRows = errors;
    },
    downloadErrors() {
      const rows = this.errorRows.map((e) => ({
        fila: e.row,
        nombre: e.fullName,
        documento: e.documentId,
        errores: e.errors.join(" | "),
      }));
      const csv = Papa.unparse(rows);
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "errores-carga-masiva.csv");
    },

    // ----- Process (Step 2) -----
    async process() {
      if (!this.canProcess) return;
      this.processing = true;
      this.result = null;
      this.$store.dispatch("fetchLoading", true);
      try {
        const payload = {
          companyId: this.companyId,
          services: this.selectedServices,
          rows: this.validRows,
        };
        this.result = await createProcessesBulk(payload);
        const { created, failed } = this.result;
        this.$swal({
          title: failed ? "Carga parcial" : "Carga completada",
          text: `Se crearon ${created} proceso(s)${failed ? ` y ${failed} fallaron` : ""}.`,
          icon: failed ? "warning" : "success",
          buttons: false,
          timer: 4000,
        });
      } catch (error) {
        console.error("Error on bulk process:", error);
        this.$swal({ text: "Ocurrió un error al procesar la carga.", icon: "error", buttons: false, timer: 3000 });
      } finally {
        this.processing = false;
        this.$store.dispatch("fetchLoading", false);
      }
    },
    downloadResultErrors() {
      const rows = (this.result?.results || [])
        .filter((r) => !r.success)
        .map((r) => ({ fila: r.row, nombre: r.fullName, documento: r.documentId, error: r.error }));
      const csv = Papa.unparse(rows);
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "errores-procesamiento-carga-masiva.csv");
    },
    goToList() {
      this.$router.push("/buscar-proceso");
    },
  },
};
</script>
<style lang="scss" scoped src="./BulkProcess.scss"></style>
